import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { OdinInput } from '@/components/ui/odin/form/odin-input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { required } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { OdinInputPassword } from '@/components/ui/odin/form/odin-input-password'
import { useModal } from 'providers/ModalProvider'
import { useAuthFlow } from 'hooks/useAuthFlow'
import { LoginModal } from 'components/modals/LoginModal'

type Props = {
  onSuccess?: () => void
}

const schema = z.object({
  email: required,
  password: required,
  name: required,
  surname: required,
})

type RegisterFormValues = z.infer<typeof schema>

export const RegisterModal = ({ onSuccess }: Props) => {
  const { t } = useTranslation()
  const { openModal, closeModal } = useModal()
  const { handleRegister, isLoading } = useAuthFlow()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '', name: '', surname: '' },
  })

  const onSubmit = async (values: RegisterFormValues) => {
    try {
      await handleRegister(values, () => {
        closeModal()
        onSuccess?.()
      })
    } catch {
      // Error already handled in hook
    }
  }

  const onClickAlreadyRegistered = () => {
    openModal(<LoginModal />)
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('register')} icon="user-plus" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={'space-y-1'}>
          <OdinInput label={t('name')} error={errors.name?.message} {...register('name')} />
          <OdinInput label={t('surname')} error={errors.surname?.message} {...register('surname')} />
          <OdinInput label={t('email')} error={errors.email?.message} {...register('email')} />
          <OdinInputPassword label={t('password')} error={errors.password?.message} {...register('password')} />
          <div className={'h-2'} />
          <div className={'cursor-pointer text-sm underline'} onClick={onClickAlreadyRegistered}>
            {t('alreadyRegistered')}
          </div>
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={isLoading}>
            <span className={'text-sm'}>{t('register')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
